// Docs: https://www.instantdb.com/docs/modeling-data

import { i } from "@instantdb/core";

const _schema = i.schema({
  entities: {
    $files: i.entity({
      path: i.string().unique().indexed(),
      url: i.string(),
    }),
    $users: i.entity({
      email: i.string().unique().indexed().optional(),
      imageURL: i.string().optional(),
      type: i.string().optional(),
    }),
    accounts: i.entity({
      secret: i.string().unique().indexed(),
    }),
    conversations: i.entity({
      lastMessageTime: i.number().indexed().optional(),
      name: i.string().indexed(),
      participants: i.json().optional(),
      participantsText: i.string().indexed().optional(),
    }),
    groups: i.entity({
      name: i.string().indexed(),
      lastActivityAt: i.number().indexed().optional(),
    }),
    tokens: i.entity({
      name: i.string(),
      createdAt: i.number(),
      revokedAt: i.number().optional(),
    }),
    userTokens: i.entity({
      name: i.string(),
      createdAt: i.number(),
      maskedToken: i.string().optional(),
    }),
    messages: i.entity({
      from: i.string().indexed(),
      text: i.string(),
      textIndexed: i.string().indexed().optional(),
      time: i.number().indexed(),
      foreignId: i.string().unique().indexed().optional(),
    }),
    snapshots: i.entity({
      createdAt: i.number().indexed(),
      messagesJson: i.string(),
      accountId: i.string().indexed(),
      conversationId: i.string().indexed().optional(),
    }),
    checkups: i.entity({
      interests: i.string(),
      sampleSize: i.number(),
      frequencyDays: i.number(),
      lastRunAt: i.number().optional(),
      groupId: i.string().indexed().optional(),
      runningAt: i.number().optional(),
      onlyStartedInPeriod: i.boolean().optional(),
      githubToken: i.string().optional(),
      githubRepo: i.string().optional(),
    }),
    reports: i.entity({
      content: i.string(),
      createdAt: i.number().indexed(),
      conversationIds: i.string(),
      viviConversationId: i.string().optional(),
    }),
    users: i.entity({
      email: i.string().unique().indexed(),
    }),
  },
  links: {
    $usersLinkedPrimaryUser: {
      forward: {
        on: "$users",
        has: "one",
        label: "linkedPrimaryUser",
        onDelete: "cascade",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "linkedGuestUsers",
      },
    },
    accountsAdmins: {
      forward: {
        on: "accounts",
        has: "many",
        label: "admins",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "accounts",
      },
    },
    conversationsAccount: {
      forward: {
        on: "conversations",
        has: "one",
        label: "account",
      },
      reverse: {
        on: "accounts",
        has: "many",
        label: "conversations",
      },
    },
    conversationsGroups: {
      forward: {
        on: "conversations",
        has: "many",
        label: "groups",
      },
      reverse: {
        on: "groups",
        has: "many",
        label: "conversations",
      },
    },
    snapshotsAccount: {
      forward: {
        on: "snapshots",
        has: "one",
        label: "account",
      },
      reverse: {
        on: "accounts",
        has: "many",
        label: "snapshots",
      },
    },
    groupsAccount: {
      forward: {
        on: "groups",
        has: "one",
        label: "account",
      },
      reverse: {
        on: "accounts",
        has: "many",
        label: "groups",
      },
    },
    tokensAccount: {
      forward: {
        on: "tokens",
        has: "one",
        label: "account",
      },
      reverse: {
        on: "accounts",
        has: "many",
        label: "tokens",
      },
    },
    tokensGroups: {
      forward: {
        on: "tokens",
        has: "many",
        label: "groups",
      },
      reverse: {
        on: "groups",
        has: "many",
        label: "tokens",
      },
    },
    userTokensUser: {
      forward: {
        on: "userTokens",
        has: "one",
        label: "user",
        onDelete: "cascade",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "userTokens",
      },
    },
    messagesConversation: {
      forward: {
        on: "messages",
        has: "one",
        label: "conversation",
      },
      reverse: {
        on: "conversations",
        has: "many",
        label: "messages",
      },
    },
    usersGroups: {
      forward: {
        on: "users",
        has: "many",
        label: "groups",
      },
      reverse: {
        on: "groups",
        has: "many",
        label: "users",
      },
    },
    checkupsGroup: {
      forward: {
        on: "checkups",
        has: "one",
        label: "group",
      },
      reverse: {
        on: "groups",
        has: "many",
        label: "checkups",
      },
    },
    checkupsExcludeGroups: {
      forward: {
        on: "checkups",
        has: "many",
        label: "excludeGroups",
      },
      reverse: {
        on: "groups",
        has: "many",
        label: "excludeCheckups",
      },
    },
    checkupsAccount: {
      forward: {
        on: "checkups",
        has: "one",
        label: "account",
      },
      reverse: {
        on: "accounts",
        has: "many",
        label: "checkups",
      },
    },
    reportsCheckup: {
      forward: {
        on: "reports",
        has: "one",
        label: "checkup",
      },
      reverse: {
        on: "checkups",
        has: "many",
        label: "reports",
      },
    },
    reportsAccount: {
      forward: {
        on: "reports",
        has: "one",
        label: "account",
      },
      reverse: {
        on: "accounts",
        has: "many",
        label: "reports",
      },
    },
  },
  rooms: {},
});

// This helps Typescript display nicer intellisense
type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
